import { BarChart3, MoreHorizontal, Search, Settings } from "lucide-react";
import * as React from "react";
import { useNavigate, useSearchParams } from "react-router";

import {
  useGetSpendCategoriesQuery,
  useGetSpendOwnersQuery,
  useGetSpendSettingsQuery,
  useGetSpendSuppliersQuery,
} from "@/api/spend";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useSuppliersAccess } from "@/hooks/use-suppliers-access";
import type { SpendSort } from "@/types/spend";
import { PeriodControls } from "./components/period-controls";
import { SpendCentreTabs } from "./components/spend-centre-tabs";
import { SupplierSpendTable } from "./components/supplier-spend-table";
import { SpendSettingsDialog } from "./components/spend-settings-dialog";
import { useSpendQueryState } from "./use-spend-query-state";

const ownerName = (owner: { name: string; surname: string; email: string }) =>
  [owner.name, owner.surname].filter(Boolean).join(" ") || owner.email;

const isSettingsRequired = (error: unknown) =>
  (error as { status?: number; data?: { detail?: { code?: string } } })?.status === 422 &&
  (error as { data?: { detail?: { code?: string } } })?.data?.detail?.code === "spend_settings_required";

export const SpendPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { isSpendCentreAdmin: isAdmin } = useSuppliersAccess();
  const [settingsOpen, setSettingsOpen] = React.useState(false);
  const {
    data: settings,
    isLoading: isSettingsLoading,
    isError: isSettingsError,
    error: settingsError,
  } = useGetSpendSettingsQuery();
  const settingsRequired = isSettingsRequired(settingsError);
  const state = useSpendQueryState(settings?.default_period_type);
  const { search: committedSearch, update, year } = state;
  const [searchInput, setSearchInput] = React.useState(committedSearch);
  React.useEffect(() => setSearchInput(committedSearch), [committedSearch]);
  React.useEffect(() => {
    const search = searchInput.trim();
    if (search === committedSearch) return;
    const timeout = window.setTimeout(() => update({ search }, true), 300);
    return () => window.clearTimeout(timeout);
  }, [searchInput, committedSearch, update]);
  const query = {
    period_type: state.periodType,
    year: state.year,
    owner_user_id: state.ownerUserId || undefined,
    category: state.category || undefined,
    search: state.search || undefined,
    sort: state.sort,
    direction: state.direction,
    offset: state.offset,
    limit: state.pageSize,
  };
  const { data, isLoading, isFetching, isError, refetch } = useGetSpendSuppliersQuery(query, {
    skip: !settings,
    refetchOnFocus: true,
    refetchOnMountOrArgChange: true,
  });
  const { data: owners = [] } = useGetSpendOwnersQuery(undefined, { refetchOnFocus: true, skip: !settings });
  const { data: categories = [] } = useGetSpendCategoriesQuery(undefined, { refetchOnFocus: true, skip: !settings });
  const years = React.useMemo(() => data?.available_years ?? [], [data?.available_years]);
  React.useEffect(() => {
    if (years.length && !years.includes(year)) {
      update(
        {
          year: years.reduce((closest, value) => (Math.abs(value - year) < Math.abs(closest - year) ? value : closest)),
        },
        true,
      );
    }
  }, [update, year, years]);
  const changeSort = (sort: SpendSort) =>
    state.update({ sort, direction: state.sort === sort && state.direction === "asc" ? "desc" : "asc" }, true);

  return (
    <main
      className="-mx-4 -my-8 min-h-screen bg-[#f7f3ee] px-5 py-7 sm:-mx-6 lg:-mx-8 lg:px-8"
      aria-label="Spend Centre workspace"
    >
      <div className="mx-auto max-w-7xl">
        <header className="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
          <div>
            <div className="mb-2 flex items-center gap-2 text-sm font-semibold text-desert-sand-700">
              <BarChart3 className="size-4" /> Supplier intelligence
            </div>
            <h1 className="text-3xl font-bold text-[#111b35]">Spend Centre</h1>
            <p className="mt-1 text-sm font-medium text-gray-500">
              Compare annual estimates with invoiced spend across accessible Contract Families.
            </p>
          </div>
          <div className="flex flex-wrap items-end gap-3" role="group" aria-label="Spend controls">
            <PeriodControls
              periodType={state.periodType}
              year={state.year}
              years={years}
              onPeriodChange={period_type => state.update({ period_type }, true)}
              onYearChange={year => state.update({ year }, true)}
            />
            {isAdmin && settings ? (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button type="button" variant="outline" size="icon" aria-label="More actions">
                    <MoreHorizontal />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-56">
                  <DropdownMenuItem className="cursor-pointer gap-2.5" onSelect={() => setSettingsOpen(true)}>
                    <Settings className="size-4 shrink-0" />
                    Spend Centre settings
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    onSelect={() => navigate(`/spend-centre/exchange-rates?${searchParams.toString()}`)}
                  >
                    Exchange rates
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            ) : null}
          </div>
        </header>
        <SpendCentreTabs active="suppliers" />
        {isSettingsLoading ? <p className="mt-8 text-sm text-gray-500">Loading Spend Centre settings…</p> : null}
        {settingsRequired ? (
          <section className="mt-6 rounded-lg border border-[#ddd5cb] bg-white p-10 text-center">
            <p className="font-semibold text-[#111b35]">
              Spend Centre is not configured. Contact your organization administrator.
            </p>
            {isAdmin ? (
              <Button className="mt-4" onClick={() => setSettingsOpen(true)}>
                Spend Centre settings
              </Button>
            ) : null}
          </section>
        ) : null}
        {isSettingsError && !settings && !settingsRequired ? (
          <section className="mt-6 rounded-lg border border-[#ddd5cb] bg-white p-10 text-center">
            <p className="text-sm text-red-700">Spend Centre settings could not be loaded.</p>
          </section>
        ) : null}
        {settings ? (
          <section
            className="mt-6 overflow-hidden rounded-lg border border-[#ddd5cb] bg-white"
            aria-label="Supplier spend"
          >
            <div className="flex flex-wrap items-center gap-4 border-b border-[#e4ddd5] px-5 py-4">
              <label className="text-xs font-semibold uppercase tracking-wide text-gray-500">
                Owner
                <select
                  aria-label="Owner"
                  value={state.ownerUserId}
                  onChange={event => state.update({ owner_user_id: event.target.value }, true)}
                  className="ml-3 h-9 min-w-52 rounded-md border border-[#d8cfc5] bg-white px-3 text-sm normal-case text-gray-800"
                >
                  <option value="">All owners</option>
                  {owners.map(owner => (
                    <option key={owner.id} value={owner.id}>
                      {ownerName(owner)}
                    </option>
                  ))}
                </select>
              </label>
              <label className="text-xs font-semibold uppercase tracking-wide text-gray-500">
                Category
                <select
                  aria-label="Category"
                  value={state.category}
                  onChange={event => state.update({ category: event.target.value }, true)}
                  className="ml-3 h-9 min-w-52 max-w-xs rounded-md border border-[#d8cfc5] bg-white px-3 text-sm normal-case text-gray-800"
                >
                  <option value="">All categories</option>
                  {categories.map(category => (
                    <option key={category} value={category}>
                      {category}
                    </option>
                  ))}
                </select>
              </label>
              <label className="relative min-w-60 flex-1 sm:max-w-sm">
                <span className="sr-only">Search suppliers and Contract Families</span>
                <Search
                  className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-gray-500"
                  aria-hidden="true"
                />
                <input
                  type="search"
                  aria-label="Search suppliers and Contract Families"
                  placeholder="Search supplier or Contract Family"
                  value={searchInput}
                  maxLength={200}
                  onChange={event => setSearchInput(event.target.value)}
                  className="h-9 w-full rounded-md border border-[#d8cfc5] bg-white pl-9 pr-3 text-sm text-gray-800 placeholder:text-gray-500 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                />
              </label>
            </div>
            {isLoading ? <div className="p-10 text-center text-sm text-gray-500">Loading Spend Centre…</div> : null}
            {isError ? (
              <div className="p-10 text-center">
                <p className="text-sm text-red-700">Spend data could not be loaded.</p>
                <Button variant="outline" size="sm" className="mt-3" onClick={() => refetch()}>
                  Try again
                </Button>
              </div>
            ) : null}
            {!isLoading && !isError && data?.items.length === 0 ? (
              <div className="p-12 text-center">
                <p className="font-semibold text-[#111b35]">No supplier spend found</p>
                <p className="mt-1 text-sm text-gray-500">Adjust the search, Owner, Category or reporting period.</p>
              </div>
            ) : null}
            {data?.items.length ? (
              <div className={isFetching ? "opacity-70" : undefined}>
                <SupplierSpendTable
                  data={data}
                  sort={state.sort}
                  direction={state.direction}
                  onSort={changeSort}
                  onOffset={offset => state.update({ offset })}
                />
              </div>
            ) : null}
          </section>
        ) : null}
        {isAdmin ? <SpendSettingsDialog open={settingsOpen} onOpenChange={setSettingsOpen} /> : null}
      </div>
    </main>
  );
};
